/**
 * Units Management Component
 * 
 * This component manages all dwelling units (apartments, villas, flats, etc.)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { unitAPI, userAPI, financialAPI, advancedAmountAPI } from '../../services/api';
import { useSettings } from '../../contexts/SettingsContext';
// import { formatDwellingUnit } from '../../utils/brandingUtils';
import { toast, Toaster } from 'react-hot-toast';
import { getCloseIconUrl } from '../../utils/closeIconUrl';
import { scrollToSectionTop } from '../../utils/scrollUtils';

const Units = () => {
  // All possible unit types for dropdowns
  const ALL_UNIT_TYPES = ['Unit', 'Villa', 'Apartment', 'Flat', 'House', 'Duplex', 'Penthouse'];

  // Navigation hook
  const navigate = useNavigate();

  // State management
  const { settings } = useSettings();
  const [units, setUnits] = useState([]);
  const [filteredUnits, setFilteredUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [owners, setOwners] = useState([]);

  // For styling
  const primaryColor = settings.theme_primary_color || '#0d6efd';
  const closeIconColor = settings.block_title_color || '#8B4513';
  const closeIconUrl = getCloseIconUrl(closeIconColor);

  // For unit naming
  // const dwellingUnitSingular = formatDwellingUnit(settings);
  // const dwellingUnitPlural = formatDwellingUnit(settings, null, true);

  // For unit details modal
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [maintenanceFee, setMaintenanceFee] = useState('');



  // For adding a new unit
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUnit, setNewUnit] = useState({
    unit_number: '',
    unit_type: settings.dwelling_unit_name || 'Unit',
    status: 'vacant',
    owner_id: null,
    maintenance_fee: '',
    size: '',
    built_month_id: '',
    transaction_start_date: '',
  });

  // For filtering
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUnitType, setFilterUnitType] = useState('all');

  // Pagination state and logic
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredUnits.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredUnits.length / itemsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    setTimeout(() => {
      scrollToSectionTop('units-table-section');
    }, 50);
  };
  const previousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      setTimeout(() => {
        scrollToSectionTop('units-table-section');
      }, 50);
    }
  };
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      setTimeout(() => {
        scrollToSectionTop('units-table-section');
      }, 50);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, searchTerm, filterUnitType, units]);

  // For delete confirmation
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [unitToDelete, setUnitToDelete] = useState(null);

  // For reset ownership confirmation
  const [showResetOwnershipConfirm, setShowResetOwnershipConfirm] = useState(false);
  const [isResettingOwnership, setIsResettingOwnership] = useState(false);

  // For change owner functionality (separate from assign/reset)
  const [showChangeOwnerModal, setShowChangeOwnerModal] = useState(false);
  const [newOwnerId, setNewOwnerId] = useState('');
  const [isChangingOwner, setIsChangingOwner] = useState(false);

  // Fetch units and owners data function - can be called to refresh data
  const fetchUnitsData = async () => {
    setLoading(true);
    try {
      let ownersData = [];
      // Fetch owners
      try {
        const ownersResponse = await userAPI.getAllUsers({ role: 'owner' });
        ownersData = ownersResponse.data;
        setOwners(ownersData);
      } catch (err) {
        console.error('Error fetching owners:', err);
      }

      // Fetch units
      try {
        const response = await unitAPI.getAllUnits();
        const unitsData = response.data;

        // Log status counts
        const occupiedCount = unitsData.filter(u => u.status === 'occupied').length;
        const vacantCount = unitsData.filter(u => u.status === 'vacant').length;
        console.log('Status counts:', { occupied: occupiedCount, vacant: vacantCount, total: unitsData.length });

        // Merge owner contact info into units data
        const enrichedUnits = unitsData.map(unit => {
          const owner = ownersData.find(o => o.id === unit.owner_id);
          return {
            ...unit,
            owner_name: owner ? owner.name : unit.owner_name,
            contact_email: owner ? owner.email : unit.contact_email,
            contact_phone: owner && owner.phone ? owner.phone : unit.contact_phone
          };
        });
        setUnits(enrichedUnits);
        setFilteredUnits(enrichedUnits);
      } catch (err) {
        console.error('Error fetching units:', err);
        setError('Failed to load units data. Please try again.');
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch units and owners on component mount
  useEffect(() => {
    fetchUnitsData();
  }, []);

  // Apply filters when filterStatus, searchTerm, or filterUnitType changes
  useEffect(() => {
    let results = [...units];

    // Debug output
    console.log('Status values:', units.map(u => u.status));
    const unitTypes = [...new Set(units.map(u => u.unit_type))];
    console.log('Available unit types:', unitTypes);
    console.log('Current unit type filter:', filterUnitType);

    // Apply status filter
    if (filterStatus !== 'all') {
      results = results.filter(unit =>
        unit.status && unit.status.toLowerCase() === filterStatus.toLowerCase()
      );
    }

    // Apply unit type filter
    if (filterUnitType !== 'all') {
      results = results.filter(unit =>
        unit.unit_type && unit.unit_type === filterUnitType
      );
    }

    // Apply search filter
    if (searchTerm.trim() !== '') {
      const search = searchTerm.toLowerCase();
      results = results.filter(unit =>
        (unit.villa_number || unit.unit_number).toLowerCase().includes(search) ||
        (unit.owner_name && unit.owner_name.toLowerCase().includes(search)) ||
        (unit.contact_email && unit.contact_email.toLowerCase().includes(search)) ||
        (unit.contact_phone && unit.contact_phone.includes(search))
      );
    }

    console.log('Filtered results count:', results.length);
    setFilteredUnits(results);
  }, [units, filterStatus, searchTerm, filterUnitType]);

  // Helper functions
  const normalizeStatus = (status) => {
    if (!status) return 'vacant';
    return status.toLowerCase() === 'occupied' ? 'occupied' : 'vacant';
  };

  // Format currency helper
  const formatCurrency = (amount) => {
    if (!amount || isNaN(amount)) return '₹0';
    return `₹${parseFloat(amount).toLocaleString('en-IN')}`;
  };



  // Get CSS class for status badges
  const getStatusBadgeClass = (status) => {
    const normalizedStatus = normalizeStatus(status);
    switch (normalizedStatus) {
      case 'occupied':
        return 'bg-success';
      case 'vacant':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  };

  // Modal handlers
  const handleOpenModal = async (unit) => {
    setShowModal(true);
    setError(null);

    // Show loading state initially with existing data
    setSelectedUnit({
      ...unit,
      status: normalizeStatus(unit.status),
      size: unit.size || '',
      bedrooms: unit.bedrooms || '',
      bathrooms: unit.bathrooms || '',
      built_year: unit.built_year || '',
      transaction_start_date: unit.transaction_start_date || ''
    });
    setMaintenanceFee(unit.maintenance_fee || '');

    // Always fetch fresh data from server
    try {
      console.log(`Fetching fresh data for unit ID: ${unit.id}`);
      const response = await unitAPI.getUnitById(unit.id);

      if (response.data) {
        const freshUnit = {
          ...response.data,
          status: normalizeStatus(response.data.status),
          // Ensure all fields are properly initialized with fresh data
          size: response.data.size || '',
          bedrooms: response.data.bedrooms || '',
          bathrooms: response.data.bathrooms || '',
          built_year: response.data.built_year || '',
          transaction_start_date: response.data.transaction_start_date || ''
        };

        console.log('Fresh unit data from server:', freshUnit);
        console.log('Size from server:', response.data.size);
        console.log('Bedrooms from server:', response.data.bedrooms);
        console.log('Bathrooms from server:', response.data.bathrooms);
        console.log('Built year from server:', response.data.built_year);

        // Merge owner details for contact info
        const owner = owners.find(o => o.id === freshUnit.owner_id);
        const enrichedUnit = {
          ...freshUnit,
          owner_name: owner ? owner.name : freshUnit.owner_name,
          contact_email: owner ? owner.email : freshUnit.contact_email,
          contact_phone: owner && owner.phone ? owner.phone : freshUnit.contact_phone
        };

        console.log('Setting enriched unit data:', enrichedUnit);
        setSelectedUnit(enrichedUnit);
        setMaintenanceFee(freshUnit.maintenance_fee || '');
      }
    } catch (err) {
      console.error('Error fetching fresh unit data:', err);
      // Fallback to initial data if fetch fails
      const normalizedUnit = {
        ...unit,
        status: normalizeStatus(unit.status),
        size: unit.size || '',
        bedrooms: unit.bedrooms || '',
        bathrooms: unit.bathrooms || '',
        built_year: unit.built_year || '',
        transaction_start_date: unit.transaction_start_date || ''
      };
      setSelectedUnit(normalizedUnit);
      setMaintenanceFee(normalizedUnit.maintenance_fee || '');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedUnit(null);
  };



  const handleOpenAddModal = () => {
    setNewUnit({
      unit_number: '',
      unit_type: settings.dwelling_unit_name || 'Unit',
      status: 'vacant',
      owner_id: null,
      maintenance_fee: settings.maintenance_fee,
      size: '',
      built_month_id: '',
      transaction_start_date: '',
    });
    setShowAddModal(true);
    setError(null);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
  };


  // Bulk update handler for Manage Unit modal
  const handleUpdateUnit = async () => {
    try {
      setLoading(true);
      setError(null);
      const { id, villa_number, unit_type, owner_id, size, bedrooms, bathrooms, built_year, built_month_id, transaction_start_date } = selectedUnit;

      // Get the original unit data to compare changes
      const originalUnit = units.find(u => u.id === id);
      const isNewOwnerAssignment = originalUnit && !originalUnit.owner_id && owner_id;
      const isTransactionStartDateSet = transaction_start_date && transaction_start_date.trim() !== '';

      // Trim validation for unit number
      const trimmedUnitNumber = villa_number ? villa_number.trim() : '';
      if (!trimmedUnitNumber) {
        setError('Please enter a unit number');
        return;
      }

      // Prevent duplicate number+type combinations with trimmed values
      if (units.some(u => u.id !== id && ((u.villa_number === trimmedUnitNumber) || (u.unit_number === trimmedUnitNumber)) && u.unit_type === unit_type)) {
        setError('A unit with this number and type already exists');
        return;
      }

      // Parse maintenance fee
      let fee = null;
      if (maintenanceFee) {
        fee = parseFloat(maintenanceFee);
        if (isNaN(fee)) {
          setError('Please enter a valid number for maintenance fee');
          return;
        }
      }

      // Validate transaction start date is required when owner is assigned and maintenance fee is set
      if (owner_id && fee && fee > 0 && (!transaction_start_date || transaction_start_date.trim() === '')) {
        setError('Transaction start date is required when assigning an owner with maintenance fee');
        return;
      }

      // Validate transaction start date is not in the future
      // if (transaction_start_date && transaction_start_date.trim() !== '') {
      //   const startDate = new Date(transaction_start_date);
      //   const currentDate = new Date();
      //   if (startDate > currentDate) {
      //     setError('Transaction start date cannot be in the future');
      //     return;
      //   }
      // }

      // Check if transaction start date is being changed or removed
      const isTransactionDateBeingChanged = originalUnit &&
        originalUnit.transaction_start_date &&
        originalUnit.transaction_start_date !== transaction_start_date;

      const isTransactionDateBeingRemoved = originalUnit &&
        originalUnit.transaction_start_date &&
        (!transaction_start_date || transaction_start_date.trim() === '');

      // Check if the transaction start date change is allowed (only on the same day it was first set)
      if (isTransactionDateBeingChanged || isTransactionDateBeingRemoved) {
        // Get the date when the unit was last updated (approximation of when transaction date was set)
        const unitLastUpdated = originalUnit.updated_at ? new Date(originalUnit.updated_at) : null;
        const today = new Date();

        // Check if the original transaction start date was set today or if we can't determine when it was set
        const isSameDay = unitLastUpdated &&
          unitLastUpdated.getFullYear() === today.getFullYear() &&
          unitLastUpdated.getMonth() === today.getMonth() &&
          unitLastUpdated.getDate() === today.getDate();

        if (!isSameDay && originalUnit.transaction_start_date) {
          setError('Transaction start date can only be changed on the same day it was first set. This restriction prevents accidental changes to financial records after they have been processed.');
          return;
        }

        // Show confirmation dialog if change is allowed
        let confirmMessage;

        if (isTransactionDateBeingRemoved) {
          confirmMessage =
            `You are removing the transaction start date.\n\n` +
            `This will:\n` +
            `• Disable all PENDING maintenance fee records for this unit\n` +
            `• No new financial records will be created\n\n` +
            `Note: Only pending records will be disabled. Paid and partially paid records will remain unchanged.\n\n` +
            `Do you want to continue?`;
        } else {
          confirmMessage =
            `You are changing the transaction start date from ${new Date(originalUnit.transaction_start_date).toLocaleDateString()} to ${new Date(transaction_start_date).toLocaleDateString()}.\n\n` +
            `This will:\n` +
            `• Disable all PENDING maintenance fee records for this unit\n` +
            `• Create new records based on the new start date\n\n` +
            `Note: Only pending records will be disabled. Paid and partially paid records will remain unchanged.\n\n` +
            `Do you want to continue?`;
        }

        const confirmChange = window.confirm(confirmMessage);

        if (!confirmChange) {
          return; // User cancelled the operation
        }
      }

      // Parse numeric fields with validation
      let parsedBedrooms = null;
      let parsedBathrooms = null;
      let parsedBuiltYear = null;

      if (bedrooms !== undefined && bedrooms !== '') {
        parsedBedrooms = parseInt(bedrooms);
        if (isNaN(parsedBedrooms) || parsedBedrooms < 0) {
          setError('Please enter a valid number for bedrooms');
          return;
        }
      }

      if (bathrooms !== undefined && bathrooms !== '') {
        parsedBathrooms = parseInt(bathrooms);
        if (isNaN(parsedBathrooms) || parsedBathrooms < 0) {
          setError('Please enter a valid number for bathrooms');
          return;
        }
      }

      if (built_year !== undefined && built_year !== '') {
        parsedBuiltYear = parseInt(built_year);
        const currentYear = new Date().getFullYear();
        if (isNaN(parsedBuiltYear) || parsedBuiltYear < 1900 || parsedBuiltYear > currentYear) {
          setError(`Please enter a valid built year between 1900 and ${currentYear}`);
          return;
        }
      }

      let parse_built_month_id = parseInt(built_month_id);

      // Update unit with all details including new fields and trimmed values
      const updateData = {
        id,
        villa_number: trimmedUnitNumber,
        unit_type,
        owner_id,
        maintenance_fee: fee,
        transaction_start_date: transaction_start_date || null,
        size: size ? size.trim() : null,
        bedrooms: parsedBedrooms,
        bathrooms: parsedBathrooms,
        built_year: parsedBuiltYear,
        built_month_id: parse_built_month_id
      };

      const updateResponse = await unitAPI.updateUnit(updateData);

      // Handle transaction start date changes and financial record management
      const isTransactionDateChanged = originalUnit && originalUnit.transaction_start_date !== transaction_start_date;
      const isTransactionDateRemoved = originalUnit && originalUnit.transaction_start_date && (!transaction_start_date || transaction_start_date.trim() === '');

      // If transaction start date is being removed, disable pending maintenance records
      if (isTransactionDateRemoved) {
        try {
          console.log('Transaction start date removed, disabling pending maintenance records...');
          const disabledRecordsCount = await removeAllMaintenanceRecords(id);

          if (disabledRecordsCount > 0) {
            toast.success(`Disabled ${disabledRecordsCount} pending maintenance records`, {
              duration: 3000,
              position: 'top-center',
              style: {
                background: '#FF9800',
                color: 'white'
              }
            });
          }
        } catch (financialError) {
          console.error('Error disabling financial records:', financialError);
        }
      }

      const shouldCreatePendingRecords = owner_id && transaction_start_date && fee && fee > 0 && (
        isNewOwnerAssignment ||
        isTransactionStartDateSet ||
        isTransactionDateChanged
      );

      console.log('=== DEBUG: Checking conditions for pending records ===');
      console.log('shouldCreatePendingRecords:', shouldCreatePendingRecords);
      console.log('isTransactionDateChanged:', isTransactionDateChanged);
      console.log('originalUnit.transaction_start_date:', originalUnit?.transaction_start_date);
      console.log('transaction_start_date (new):', transaction_start_date);
      console.log('isNewOwnerAssignment:', isNewOwnerAssignment);
      console.log('Condition for disable: isTransactionDateChanged && originalUnit.transaction_start_date && !isNewOwnerAssignment =', 
        isTransactionDateChanged && originalUnit?.transaction_start_date && !isNewOwnerAssignment);

      if (shouldCreatePendingRecords) {
        // Step 1: Disable old pending records if transaction date is being changed
        // Call whenever transaction date changes (removed isNewOwnerAssignment check)
        if (isTransactionDateChanged && originalUnit.transaction_start_date) {
          try {
            console.log('>>> CALLING removeAllMaintenanceRecords for unit:', id);
            const disabledRecordsCount = await removeAllMaintenanceRecords(id);

            if (disabledRecordsCount > 0) {
              toast.success(`Disabled ${disabledRecordsCount} pending records due to date change`, {
                duration: 3000,
                position: 'top-center',
                style: {
                  background: '#FF9800',
                  color: 'white'
                }
              });
            }
          } catch (disableError) {
            console.error('Error disabling pending financial records:', disableError);
            // Continue with creating new records even if disabling fails
          }
        }

        // Step 2: Create new pending records
        try {
          console.log('Creating pending financial records for unit:', id);
          await createPendingFinancialRecords(id, transaction_start_date, fee);
        } catch (createError) {
          console.error('Error creating pending financial records:', createError);
          // Don't fail the entire update if financial record creation fails
          toast.success('Unit updated successfully, but there was an issue creating financial records. Please check the financial section.', {
            duration: 5000,
            position: 'top-center',
            style: {
              background: '#FF9800',
              color: 'white'
            }
          });
        }
      }

      // Fetch fresh data from server to ensure we have the latest 
      const freshUnitResponse = await unitAPI.getUnitById(id);

      // Update local state with fresh data
      const selectedOwner = owners.find(owner => owner.id === owner_id);
      const freshUnit = freshUnitResponse.data;

      const updatedUnits = units.map(u =>
        u.id === id ? {
          ...freshUnit, // Use fresh data from server
          owner_name: selectedOwner ? selectedOwner.name : freshUnit.owner_name,
          contact_email: selectedOwner ? selectedOwner.email : freshUnit.contact_email,
          contact_phone: selectedOwner && selectedOwner.phone ? selectedOwner.phone : freshUnit.contact_phone,
        } : u
      );
      setUnits(updatedUnits);

      // Update selectedUnit state in modal with fresh data
      setSelectedUnit({
        ...freshUnit,
        owner_name: selectedOwner ? selectedOwner.name : freshUnit.owner_name,
        contact_email: selectedOwner ? selectedOwner.email : freshUnit.contact_email,
        contact_phone: selectedOwner && selectedOwner.phone ? selectedOwner.phone : freshUnit.contact_phone,
      });

      // Update maintenance fee state to match fresh data
      setMaintenanceFee(freshUnit.maintenance_fee || '');

      // Also refresh the main units list to ensure consistency
      try {
        const updatedUnitsResponse = await unitAPI.getAllUnits();
        const enrichedUnits = updatedUnitsResponse.data.map(unit => {
          const owner = owners.find(o => o.id === unit.owner_id);
          return {
            ...unit,
            owner_name: owner ? owner.name : unit.owner_name,
            contact_email: owner ? owner.email : unit.contact_email,
            contact_phone: owner && owner.phone ? owner.phone : unit.contact_phone
          };
        });
        setUnits(enrichedUnits);
        console.log('Refreshed units list after update');
      } catch (refreshError) {
        console.error('Error refreshing units list:', refreshError);
      }

      toast.success('Unit updated successfully', {
        duration: 3000,
        position: 'top-center',
        style: {
          background: '#4CAF50',
          color: 'white'
        }
      });
      setShowModal(false);

      // Force a small delay and then refresh the page to ensure data persistence
      // setTimeout(() => {
      //   window.location.reload();
      // }, 1000);
    } catch (err) {
      console.error('Error bulk updating unit:', err);
      setError('Failed to update unit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter reset handler
  const resetFilters = () => {
    setFilterStatus('all');
    setFilterUnitType('all');
    setSearchTerm('');
  };

  // Handle adding a new unit
  const handleAddUnit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate inputs
      if (!newUnit.unit_number || !newUnit.unit_number.trim()) {
        setError('Please enter a unit number');
        return;
      }

      // Check for duplicates
      if (units.some(unit => unit.villa_number === newUnit.unit_number)) {
        setError('A unit with this number already exists');
        return;
      }

      // Parse maintenance fee
      let numericFee = null;
      if (newUnit.maintenance_fee) {
        if (newUnit.maintenance_fee === '0' || newUnit.maintenance_fee === '0.0' || newUnit.maintenance_fee === '0.00') {
          numericFee = 0;
        } else {
          numericFee = parseFloat(newUnit.maintenance_fee);
          if (isNaN(numericFee)) {
            setError('Please enter a valid number for maintenance fee');
            return;
          }
        }
      }

      // Prepare unit data
      const unitData = {
        villa_number: newUnit.unit_number.trim(),
        unit_type: newUnit.unit_type,
        maintenance_fee: numericFee,
        size: newUnit.size || null,
        built_month_id: newUnit.built_month_id || null,
      };

      console.log('Creating new unit:', unitData);

      // Call API to create unit
      const response = await unitAPI.createUnit(unitData);
      console.log('Unit creation response:', response.data);

      // Refresh unit list with enriched data (including owner contact info)
      const updatedUnitsResponse = await unitAPI.getAllUnits();
      const enrichedUnits = updatedUnitsResponse.data.map(unit => {
        const owner = owners.find(o => o.id === unit.owner_id);
        return {
          ...unit,
          owner_name: owner ? owner.name : unit.owner_name,
          contact_email: owner ? owner.email : unit.contact_email,
          contact_phone: owner && owner.phone ? owner.phone : unit.contact_phone
        };
      });
      setUnits(enrichedUnits);

      // Success message
      toast.success('Unit added successfully', {
        duration: 3000,
        position: 'top-center',
        style: {
          background: '#4CAF50',
          color: 'white'
        }
      });

      // Reset form and close modal
      setNewUnit({
        unit_number: '',
        unit_type: settings.dwelling_unit_name || 'Unit',
        status: 'vacant',
        owner_id: null,
        maintenance_fee: '',
        size: '',
        built_month_id: ''
      });
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding unit:', error);

      // Check for plan limit errors
      if (error.response?.status === 403 && error.response.data?.upgrade_required) {
        setError(error.response.data.message || 'Unit limit reached. Please upgrade your plan.');
      } else {
        setError((error.response?.data?.message || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  // Handler to delete a unit
  const handleDeleteUnit = async () => {
    if (!unitToDelete) return;
    try {
      setLoading(true);
      await unitAPI.deleteUnit(unitToDelete.id);
      // Remove from lists
      setUnits(units.filter(u => u.id !== unitToDelete.id));
      setFilteredUnits(filteredUnits.filter(u => u.id !== unitToDelete.id));
      toast.success(`Unit ${unitToDelete.unit_number || unitToDelete.villa_number} deleted successfully`);
    } catch (err) {
      console.error('Error deleting unit:', err);
      setError('Failed to delete unit. Please try again.');
    } finally {
      setLoading(false);
      setShowDeleteConfirmation(false);
      setUnitToDelete(null);
    }
  };

  // Handle reset ownership of a unit
  const handleResetOwnership = async () => {
    if (!selectedUnit?.id) return;
    
    try {
      setIsResettingOwnership(true);
      console.log('Resetting ownership for unit:', selectedUnit.id);
      
      const response = await unitAPI.resetOwnership(selectedUnit.id);
      console.log('Reset ownership response:', response);
      
      const disabledCount = response.data?.disabledFinancialRecords || 0;
      
      // Update the unit in local state
      const updatedUnit = {
        ...selectedUnit,
        owner_id: null,
        owner_name: null,
        transaction_start_date: null,
        status: 'vacant',
        built_year: null,
        built_month_id: null,
        visitor_approval_required: 0,
        contact_phone: null,
        contact_email: null
      };
      
      setSelectedUnit(updatedUnit);
      
      // Update the units list
      setUnits(units.map(u => u.id === selectedUnit.id ? updatedUnit : u));
      setFilteredUnits(filteredUnits.map(u => u.id === selectedUnit.id ? updatedUnit : u));
      
      // Reset maintenance fee field
      setMaintenanceFee('');
      
      toast.success(
        `Ownership reset successfully!${disabledCount > 0 ? ` ${disabledCount} pending transactions disabled.` : ''}`
      );
      
      // Close the confirmation modal
      setShowResetOwnershipConfirm(false);
      
      // Close the edit modal
      setShowModal(false);
      setSelectedUnit(null);
      
    } catch (error) {
      console.error('Error resetting ownership:', error);
      toast.error(error.response?.data?.message || 'Failed to reset ownership. Please try again.');
    } finally {
      setIsResettingOwnership(false);
    }
  };

  // Handle change owner - separate function using new API endpoint
  // This only updates owner_id in villas table without affecting other functionality
  const handleChangeOwner = async () => {
    if (!selectedUnit?.id || !newOwnerId) return;
    
    try {
      setIsChangingOwner(true);
      console.log('Changing owner for unit:', selectedUnit.id, 'to new owner:', newOwnerId);
      
      // Call the new changeOwner API which only updates owner_id
      const response = await unitAPI.changeOwner(selectedUnit.id, parseInt(newOwnerId));
      console.log('Change owner response:', response);
      
      toast.success('Owner changed successfully!');
      
      // Close the change owner modal
      setShowChangeOwnerModal(false);
      setNewOwnerId('');
      
      // Close the edit modal
      setShowModal(false);
      setSelectedUnit(null);
      
      // Refresh units data to get updated information
      await fetchUnitsData();
      
    } catch (error) {
      console.error('Error changing owner:', error);
      toast.error(error.response?.data?.message || 'Failed to change owner. Please try again.');
    } finally {
      setIsChangingOwner(false);
    }
  };

  // Function to disable pending maintenance fee records for a unit (set payment_status_TF = 0)
  // Instead of deleting records, we mark pending records as inactive
  const removeAllMaintenanceRecords = async (unitId) => {
    try {
      console.log(`Disabling pending maintenance fee records for unit ${unitId}`);

      // Call the API to disable pending records (set payment_status_TF = 0 for status = 'pending')
      const response = await financialAPI.disablePendingByUnit(unitId);
      console.log('Disable pending API response:', response);
      const disabledCount = response.data?.disabledCount || 0;

      if (disabledCount > 0) {
        console.log(`Successfully disabled ${disabledCount} pending maintenance fee records for unit ${unitId}`);
      } else {
        console.log('No pending maintenance records found to disable');
      }

      return disabledCount;

    } catch (error) {
      console.error('Error disabling pending maintenance fee records:', error);
      console.error('Error details:', error.response?.data || error.message);
      // Return 0 instead of throwing - let the process continue
      return 0;
    }
  };

  // Function to remove auto-generated financial records for a unit
  const removeAutoGeneratedFinancialRecords = async (unitId) => {
    try {
      console.log(`Removing auto-generated financial records for unit ${unitId}`);

      // Get existing financial records for this unit
      const existingFinancialsResponse = await financialAPI.getAllFinancials();

      // Get all maintenance fee records for this unit (more comprehensive filter)
      const autoGeneratedRecords = existingFinancialsResponse.data.filter(
        transaction =>
          transaction.unit_id === unitId &&
          transaction.type === 'payment' &&
          transaction.category === 'maintenance' &&
          (
            // Auto-generated records with specific remarks
            transaction.remarks === 'Auto-generated pending maintenance fee' ||
            // Records that are still pending (likely auto-generated)
            (transaction.status === 'pending' && transaction.category === 'maintenance') ||
            // Records with remaining amount equal to full amount (not partially paid manually)
            (transaction.remaining_amount && parseFloat(transaction.remaining_amount) === parseFloat(transaction.amount))
          )
      );

      console.log(`Found ${autoGeneratedRecords.length} records to remove for unit ${unitId}:`, autoGeneratedRecords);

      // Delete each auto-generated record
      const deletePromises = autoGeneratedRecords.map(async (record) => {
        try {
          await financialAPI.deleteFinancial(record.id);
          console.log(`Deleted auto-generated record ID: ${record.id}, Date: ${record.date}, Amount: ${record.amount}`);
        } catch (err) {
          console.error(`Failed to delete financial record ${record.id}:`, err);
        }
      });

      await Promise.all(deletePromises);

      if (autoGeneratedRecords.length > 0) {
        console.log(`Successfully removed ${autoGeneratedRecords.length} auto-generated financial records`);
      } else {
        console.log('No auto-generated records found to remove');
      }

      return autoGeneratedRecords.length;

    } catch (error) {
      console.error('Error removing auto-generated financial records:', error);
      throw error;
    }
  };

  // Function to create pending financial records from transaction start date to current month
  const createPendingFinancialRecords = async (unitId, transactionStartDate, maintenanceFeeAmount) => {
    try {
      const startDate = new Date(transactionStartDate);
      const currentDate = new Date();

      // Get existing financial records for this unit to avoid duplicates
      const existingFinancialsResponse = await financialAPI.getAllFinancials();
      const existingUnitTransactions = existingFinancialsResponse.data.filter(
        transaction => transaction.unit_id === unitId && transaction.type === 'payment'
      );

      // Create a set of existing month-year combinations for this unit
      const existingMonthYears = new Set();
      existingUnitTransactions.forEach(transaction => {
        const transactionDate = new Date(transaction.date);
        const monthYear = `${transactionDate.getFullYear()}-${transactionDate.getMonth() + 1}`;
        existingMonthYears.add(monthYear);
      });

      // Generate pending records for each month from start date to current month
      const recordsToCreate = [];
      let iterDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1); // Start from first day of the month

      while (iterDate <= currentDate) {
        const monthYear = `${iterDate.getFullYear()}-${iterDate.getMonth() + 1}`;

        // Only create record if it doesn't already exist
        if (!existingMonthYears.has(monthYear)) {
          const transactionData = {
            type: 'payment',
            amount: parseFloat(maintenanceFeeAmount),
            category: 'maintenance',
            unit_id: unitId,
            payment_date: `${iterDate.getFullYear()}-${String(iterDate.getMonth() + 1).padStart(2, '0')}-01`, // First day of the month
            date: `${iterDate.getFullYear()}-${String(iterDate.getMonth() + 1).padStart(2, '0')}-01`, // First day of the month
            month_id: iterDate.getMonth() + 1, // 1-12
            remarks: 'Auto-generated pending maintenance fee',
            status: 'pending',
            remaining_amount: parseFloat(maintenanceFeeAmount)
          };

          recordsToCreate.push(transactionData);
        }

        // Move to next month
        iterDate.setMonth(iterDate.getMonth() + 1);
      }

      // Create all the pending records
      console.log(`Creating ${recordsToCreate.length} pending financial records for unit ${unitId}`);

      for (const recordData of recordsToCreate) {
        try {
          await financialAPI.createFinancial(recordData);
          console.log(`Created pending record for ${recordData.date}`);
        } catch (err) {
          console.error(`Failed to create financial record for ${recordData.date}:`, err);
          // Continue with other records even if one fails
        }
      }

      if (recordsToCreate.length > 0) {
        toast.success(`Created ${recordsToCreate.length} pending maintenance fee records`, {
          duration: 4000,
          position: 'top-center',
          style: {
            background: '#4CAF50',
            color: 'white'
          }
        });
      } else {
        console.log('No new pending records needed - all months already have transactions');
      }

    } catch (error) {
      console.error('Error creating pending financial records:', error);
      throw error; // Re-throw to be handled by caller
    }
  };


  function formatDateIST(dateString) {
    if (!dateString || typeof dateString !== 'string') return 'N/A';

    try {
      // Parse "YYYY-MM-DD HH:mm:ss" as UTC
      const [datePart] = dateString.split(' ');
      const [year, month, day] = datePart.split('-').map(Number);

      // Create a UTC Date object
      const utcDate = new Date(Date.UTC(year, month - 1, day));

      // Format to DD/MM/YYYY in IST timezone
      return new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(utcDate);
    } catch (err) {
      console.error('Invalid date string:', dateString, err);
      return 'Invalid Date';
    }
  }

  // Get all owner IDs who already have a unit assigned
  const assignedOwnerIds = units
    .filter(unit => unit.owner_id !== null)
    .map(unit => unit.owner_id);
  const unassignedOwners = owners.filter(owner => !assignedOwnerIds.includes(owner.id));

  console.log('Unassigned owners:', unassignedOwners);

  const months = [
    { id: 1, name: 'January' },
    { id: 2, name: 'February' },
    { id: 3, name: 'March' },
    { id: 4, name: 'April' },
    { id: 5, name: 'May' },
    { id: 6, name: 'June' },
    { id: 7, name: 'July' },
    { id: 8, name: 'August' },
    { id: 9, name: 'September' },
    { id: 10, name: 'October' },
    { id: 11, name: 'November' },
    { id: 12, name: 'December' }
  ];

  // Loading state
  if (loading && units.length === 0) {
    return (
      <div className="container-fluid mt-4 activity-container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="page-title">
            <i className="fas fa-home"></i> Units Management
          </h1>
        </div>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading units data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4 activity-container">
      {/* Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
        <h1 className="page-title">
          <i className="fas fa-home"></i> Units Management
        </h1>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <button
            className="btn btn-outline-success"
            onClick={handleOpenAddModal}
          >
            <i className="fas fa-plus me-2"></i>
            Add New Unit
          </button>
          <div className="unit-count-badge">
            {units.length} Units
          </div>
        </div>
      </div>

      {/* Error display */}
      {/* {error && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </div>
      )} */}

      {/* Filters */}
      <div className="filters-container flex-wrap mb-4">
        <div className="search-box">
          <div className="input-group">
            <span className="input-group-text bg-light">
              <i className="fas fa-search"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by number, owner..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="filter-options d-flex flex-wrap gap-3">
          <div className="filter-dropdown">
            <div className="dropdown">
              <button className="btn btn-outline-secondary dropdown-toggle" type="button" id="statusFilterDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                {filterStatus === 'all' ? 'All Units' : (filterStatus === 'occupied' ? 'Occupied Only' : 'Vacant Only')}
              </button>
              <ul className="dropdown-menu" aria-labelledby="statusFilterDropdown">
                <li><button className="dropdown-item" onClick={() => setFilterStatus('all')}>All Units</button></li>
                <li><button className="dropdown-item" onClick={() => setFilterStatus('occupied')}>Occupied Only</button></li>
                <li><button className="dropdown-item" onClick={() => setFilterStatus('vacant')}>Vacant Only</button></li>
              </ul>
            </div>
          </div>

          <div className="filter-dropdown">
            <div className="dropdown">
              <button className="btn btn-outline-secondary dropdown-toggle" type="button" id="typeFilterDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                {filterUnitType === 'all' ? 'All Types' : `${filterUnitType}s Only`}
              </button>
              <ul className="dropdown-menu" aria-labelledby="typeFilterDropdown">
                <li><button className="dropdown-item" onClick={() => setFilterUnitType('all')}>All Types</button></li>
                {ALL_UNIT_TYPES.map(type => (
                  <li key={type}>
                    <button className="dropdown-item" onClick={() => setFilterUnitType(type)}>
                      {type}s Only
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="status-counts d-flex flex-wrap">
            <span className="badge bg-success" onClick={() => setFilterStatus('occupied')}>
              {units.filter(u => u.status === 'occupied').length} Occupied
            </span>
            <span className="badge bg-danger ms-2" onClick={() => setFilterStatus('vacant')}>
              {units.filter(u => u.status === 'vacant').length} Vacant
            </span>
          </div>

          <button
            className="btn btn-sm btn-outline-secondary searchCloseFilter"
            onClick={resetFilters}
            title="Clear all filters"
          >
            <i className="fas fa-times-circle me-1"></i>
            Clear Filters
          </button>
        </div>
      </div>

      {/* Units Table View */}
      <div className="table-responsive" id="units-table-section">
        <table className="table table-striped table-hover align-middle units-table">
          <thead className="table-dark">
            <tr>
              <th>Number</th>
              <th>Type</th>
              <th>Size</th>
              <th className="hide-mobile">Owner</th>
              <th className="hide-mobile">Contact</th>
              <th>Status</th>
              <th className="hide-mobile">Maintenance Fee</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUnits.length > 0 ? (
              currentItems.map(unit => (
                <tr key={unit.id}>
                  <td className="fw-bold text-primary">{unit.villa_number}</td>
                  <td>{unit.unit_type}</td>
                  <td className="text-muted">{unit.size || 'Not specified'}</td>
                  <td className="hide-mobile">
                    {unit.owner_name ? (
                      <div>
                        <div className="fw-semibold">{unit.owner_name}</div>
                        <small className="text-muted">{unit.owner_email}</small>
                      </div>
                    ) : (
                      <span className="text-muted fst-italic">No owner assigned</span>
                    )}
                  </td>
                  <td className="hide-mobile">
                    {unit.contact_phone ? (
                      <span className="text-nowrap">{unit.contact_phone}</span>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>
                    <span className={`badge rounded-pill ${getStatusBadgeClass(unit.status)}`}>
                      {unit.status === 'occupied' ? 'Occupied' :
                        unit.status === 'vacant' ? 'Vacant' : 'Available'}
                    </span>
                  </td>
                  <td className="hide-mobile">
                    {unit.maintenance_fee ? `₹${unit.maintenance_fee}/month` : 'Not set'}
                  </td>
                  <td className="text-end">
                    <div className="btn-group btn-group-sm" role="group">
                      <button
                        className="btn btn-outline-secondary btn-sm justify-content-center"
                        onClick={() => navigate(`/admin/units/${unit.id}`)}
                        title="View Unit Details"
                      >
                        <i className="fas fa-eye"></i>
                        <span className="d-lg-inline ms-1">View</span>
                      </button>
                      <button
                        className="btn btn-outline-primary btn-sm justify-content-center"
                        onClick={() => handleOpenModal(unit)}
                        title="Edit Unit"
                      >
                        <i className="fas fa-edit"></i>
                        <span className="d-lg-inline ms-1">Edit</span>
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm justify-content-center"
                        onClick={() => {
                          setUnitToDelete(unit);
                          setShowDeleteConfirmation(true);
                        }}
                        title="Delete Unit"
                      >
                        <i className="fas fa-trash"></i>
                        <span className="d-lg-inline ms-1">Delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center py-4 text-muted">
                  <i className="fas fa-building fa-3x mb-3 d-block"></i>
                  <p>No units found matching your criteria.</p>
                  <p>Try adjusting your search or filters.</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div>
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredUnits.length)} of {filteredUnits.length} units
          </div>
          <nav aria-label="Units pagination">
            <ul className="pagination mb-0">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={previousPage} disabled={currentPage === 1}>
                  <i className="fas fa-chevron-left"></i>
                </button>
              </li>
              {Array.from({ length: totalPages }).map((_, index) => {
                const pageNumber = index + 1;
                if (
                  pageNumber === 1 ||
                  pageNumber === totalPages ||
                  (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                ) {
                  return (
                    <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => paginate(pageNumber)}>
                        {pageNumber}
                      </button>
                    </li>
                  );
                }
                if (
                  (pageNumber === 2 && currentPage > 3) ||
                  (pageNumber === totalPages - 1 && currentPage < totalPages - 2)
                ) {
                  return (
                    <li key={pageNumber} className="page-item disabled">
                      <span className="page-link">...</span>
                    </li>
                  );
                }
                return null;
              })}
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={nextPage} disabled={currentPage === totalPages}>
                  <i className="fas fa-chevron-right"></i>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      {/* CSS Styles */}
      <style>
        {`
          /* Override Bootstrap's primary color with theme color */
          :root {
            --bs-primary: ${primaryColor} !important;
            --bs-primary-rgb: ${parseInt(primaryColor.slice(1, 3), 16)}, ${parseInt(primaryColor.slice(3, 5), 16)}, ${parseInt(primaryColor.slice(5, 7), 16)} !important;
          }
          
          .btn-primary {
            background-color: ${primaryColor} !important;
            border-color: ${primaryColor} !important;
          }
          
          .unit-count-badge {
            background-color: ${primaryColor};
            color: #fff;
            padding: 0.5rem 0.75rem;
            font-size: 0.875rem;
            border-radius: 50rem;
            font-weight: 600;
          }
          
          /* Filter styles */
          .filters-container {
            display: flex;
            flex-direction: column;
            gap: 16px;
            background-color: #f9f9fa;
            border-radius: 8px;
            padding: 16px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          }
          
          @media (min-width: 768px) {
            .filters-container {
              flex-direction: row;
              align-items: center;
              justify-content: space-between;
            }
            
            .search-box {
              flex: 1;
              max-width: 400px;
            }
          }
          
          /* Units Table Styles */
          .units-table {
            margin-bottom: 0;
            background: white;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }
          
          .units-table thead th {
            border: none;
            font-weight: 600;
            font-size: 14px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            padding: 12px 16px;
          }
          
          .units-table tbody td {
            border-top: 1px solid #e9ecef;
            padding: 8px 16px;
            vertical-align: middle;
          }
          
          .unit-row.vacant-row {
            border-left: 4px solid #dc3545;
          }
          
          .unit-row.occupied-row {
            border-left: 4px solid #198754;
          }
          
          .unit-row:hover {
            background-color: #f8f9fa;
          }
          
          .unit-cell {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }
          
          .unit-number {
            font-size: 16px;
            font-weight: 600;
            color: #333;
            margin: 0;
          }
          
          .unit-meta {
            display: flex;
            gap: 6px;
            align-items: center;
          }
          
          .unit-type-badge {
            background-color: #6c757d;
            color: white;
            padding: 2px 6px;
            border-radius: 3px;
            font-size: 10px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            font-weight: 500;
          }
          
          .unit-status-badge {
            padding: 2px 6px;
            border-radius: 3px;
            font-size: 10px;
            font-weight: 500;
            text-transform: uppercase;
          }
          
          .status-occupied {
            background-color: #198754;
            color: white;
          }
          
          .status-vacant {
            background-color: #dc3545;
            color: white;
          }
          
          .owner-cell {
            min-height: 40px;
            display: flex;
            align-items: center;
          }
          
          .owner-name {
            font-weight: 500;
            color: #333;
            margin: 0;
          }
          
          .contact-cell {
            display: flex;
            flex-direction: column;
            gap: 2px;
            min-height: 40px;
            justify-content: center;
          }
          
          .contact-item {
            display: flex;
            align-items: center;
            font-size: 13px;
            color: #666;
          }
          
          .contact-item i {
            width: 12px;
            color: #999;
          }
          
          .maintenance-cell {
            min-height: 40px;
            display: flex;
            align-items: center;
          }
          
          .maintenance-amount {
            font-weight: 500;
            color: #333;
          }
          
          .actions-cell {
            display: flex;
            gap: 4px;
            align-items: center;
            min-height: 40px;
          }
          
          .actions-cell .btn {
            font-size: 12px;
            padding: 4px 8px;
            border-radius: 4px;
          }
          
          .actions-cell .btn i {
            margin-right: 4px;
          }
          
          /* Responsive adjustments */
          @media (max-width: 768px) {
            .units-table thead th,
            .units-table tbody td {
              padding: 8px 12px;
            }
            
            .contact-cell {
              font-size: 12px;
            }
            
            .actions-cell {
              flex-direction: column;
              gap: 2px;
            }
            
            .actions-cell .btn {
              font-size: 11px;
              padding: 3px 6px;
            }
          }
        `}
      </style>

      {/* Unit Details Modal */}
      {showModal && selectedUnit && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: settings.block_title_bg_color || '#f8f9fa', color: settings.block_title_color || '#8B4513' }}>
                <h5 className="modal-title">
                  Manage {selectedUnit.unit_type || 'Unit'} #{selectedUnit.villa_number || selectedUnit.unit_number}
                </h5>
                <button type="button" className="btn-close" onClick={handleCloseModal}
                  style={{ backgroundImage: closeIconUrl, fontSize: '18px', opacity: '0.9' }}
                ></button>
              </div>
              <div className="modal-body">
                {error && (
                  <div className="alert alert-danger">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {error}
                  </div>
                )}

                {/* Status Information */}
                <div className="card mb-4">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">Current Status</h6>
                  </div>
                  <div className="card-body">
                    <span className={`badge ${selectedUnit.status === 'vacant' ? 'bg-danger' : 'bg-success'} mb-0 fs-6`}>
                      {selectedUnit.status === 'vacant' ? 'Vacant' : 'Occupied'}
                    </span>
                  </div>
                </div>

                {/* Unit Details Section */}
                <div className="card mb-4">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">Unit Details</h6>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="unit_number" className="form-label">Unit Number</label>
                          <input
                            type="text"
                            className="form-control"
                            id="unit_number"
                            value={selectedUnit.villa_number || ''}
                            onChange={(e) => setSelectedUnit({ ...selectedUnit, villa_number: e.target.value })}
                            placeholder="e.g. A-101"
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="unit_type" className="form-label">Unit Type</label>
                          <select
                            className="form-select"
                            id="unit_type"
                            value={selectedUnit.unit_type || ''}
                            onChange={(e) => setSelectedUnit({ ...selectedUnit, unit_type: e.target.value })}
                          >
                            {ALL_UNIT_TYPES.map(type => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="size" className="form-label">Size</label>
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control"
                              id="size"
                              value={selectedUnit.size ? selectedUnit.size.replace(' sq ft', '').replace('sq ft', '').trim() : ''}
                              onChange={(e) => setSelectedUnit({ ...selectedUnit, size: e.target.value ? `${e.target.value} sq ft` : '' })}
                              placeholder="1200"
                              min="0"
                              onInput={(e) => {
                                e.target.value = e.target.value.replace(/[^0-9]/g, '');
                              }}
                            />
                            <span className="input-group-text">sq ft</span>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="built_year" className="form-label">Built Year</label>
                          <input
                            type="text"
                            className="form-control"
                            id="built_year"
                            value={selectedUnit.built_year || ''}
                            onChange={(e) => setSelectedUnit({ ...selectedUnit, built_year: e.target.value })}
                            placeholder="e.g. 2020"
                            min="1900"
                            max={new Date().getFullYear()}
                            onInput={(e) => {
                              e.target.value = e.target.value.replace(/[^0-9]/g, '');
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="built_month_id" className="form-label">Built Month</label>
                          <select
                            className="form-select"
                            id="built_month_id"
                            value={selectedUnit.built_month_id || ''}
                            onChange={(e) => setSelectedUnit({ ...selectedUnit, built_month_id: e.target.value })}
                          >
                            {months.map((month) => (
                              <option key={month.id} value={month.id}>
                                {month.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="bedrooms" className="form-label">Bedrooms</label>
                          <input
                            type="text"
                            className="form-control"
                            id="bedrooms"
                            value={selectedUnit.bedrooms || ''}
                            onChange={(e) => setSelectedUnit({ ...selectedUnit, bedrooms: e.target.value })}
                            placeholder="e.g. 3"
                            min="0"
                            onInput={(e) => {
                              e.target.value = e.target.value.replace(/[^0-9]/g, '');
                            }}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="bathrooms" className="form-label">Bathrooms</label>
                          <input
                            type="text"
                            className="form-control"
                            id="bathrooms"
                            value={selectedUnit.bathrooms || ''}
                            onChange={(e) => setSelectedUnit({ ...selectedUnit, bathrooms: e.target.value })}
                            placeholder="e.g. 2"
                            min="0"
                            onInput={(e) => {
                              e.target.value = e.target.value.replace(/[^0-9]/g, '');
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Owner Assignment Section */}
                <div className="card mb-4">
                  <div className="card-header bg-light d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Owner Information</h6>
                    {/* Change Owner and Reset Ownership Buttons - Only show if owner is assigned */}
                    {(() => {
                      const originalUnit = units.find(u => u.id === selectedUnit.id);
                      return originalUnit?.owner_id ? (
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => {
                              setNewOwnerId('');
                              setShowChangeOwnerModal(true);
                            }}
                          >
                            <i className="bi bi-person-gear me-1"></i>
                            Change Owner
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => setShowResetOwnershipConfirm(true)}
                          >
                            <i className="bi bi-person-x me-1"></i>
                            Reset Ownership
                          </button>
                        </div>
                      ) : null;
                    })()}
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        {selectedUnit.owner_id ? (
                          <div>
                            <p className="mb-1"><strong>Name:</strong> {selectedUnit.owner_name}</p>
                            <p className="mb-1"><strong>Email:</strong> {selectedUnit.contact_email || selectedUnit.owner_email}</p>
                            <p className="mb-0"><strong>Phone:</strong> {selectedUnit.contact_phone || 'Not provided'}</p>
                          </div>
                        ) : (
                          <p className="text-muted">No owner assigned</p>
                        )}
                      </div>
                      {!units.find(u => u.id === selectedUnit.id)?.owner_id && (
                      <div className="col-md-6">
                        <select
                          className="form-select mb-3"
                          value={selectedUnit.owner_id || ''}
                          onChange={(e) => {
                            const selectedOwnerId = e.target.value ? parseInt(e.target.value) : null;
                            const selectedOwner = owners.find(owner => owner.id === selectedOwnerId);

                            setSelectedUnit({
                              ...selectedUnit,
                              owner_id: selectedOwnerId,
                              owner_name: selectedOwner ? selectedOwner.name : '',
                              contact_email: selectedOwner ? selectedOwner.email : '',
                              contact_phone: selectedOwner ? selectedOwner.phone : ''
                            });
                          }}
                        >
                          <option value="">-- Unassigned --</option>
                          {unassignedOwners.map(owner => (
                            <option key={owner.id} value={owner.id}>
                              {owner.name} ({owner.email})
                            </option>
                          ))}
                        </select>
                      </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Maintenance Fee Section */}
                <div className="card mb-4">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">Monthly Maintenance Fee</h6>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">

                        <label htmlFor="maintenance_fee" className="form-label"> Fee</label>
                        <div className="input-group">
                          <span className="input-group-text">₹</span>

                          <input
                            type="text"
                            className="form-control"
                            placeholder="Amount"
                            value={maintenanceFee}
                            onChange={(e) => setMaintenanceFee(e.target.value)}
                            onInput={(e) => {
                              e.target.value = e.target.value.replace(/[^0-9]/g, '');
                            }}
                          />
                          <span className="input-group-text">/month</span>
                        </div>
                        <small className="form-text text-muted d-block mt-2">
                          This fee will be displayed to the owner in their payment section.
                          Leave blank or zero to use the default maintenance fee from society settings.
                        </small>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="transaction_start_date" className="form-label">
                            Transaction Start Date
                            {selectedUnit.owner_id && maintenanceFee && parseFloat(maintenanceFee) > 0 && (
                              <span className="text-danger ms-1">*</span>
                            )}
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            id="transaction_start_date"
                            value={selectedUnit.transaction_start_date || ''}
                            onChange={(e) => setSelectedUnit({ ...selectedUnit, transaction_start_date: e.target.value })}
                            // max={new Date().toISOString().split('T')[0]} // Cannot select future dates
                            disabled={(() => {
                              // Check if transaction start date exists and if it's not the same day as unit was last updated
                              const originalUnit = units.find(u => u.id === selectedUnit.id);
                              if (!originalUnit || !originalUnit.transaction_start_date) return false;

                              const unitLastUpdated = originalUnit.updated_at ? new Date(originalUnit.updated_at) : null;
                              const today = new Date();

                              const isSameDay = unitLastUpdated &&
                                unitLastUpdated.getFullYear() === today.getFullYear() &&
                                unitLastUpdated.getMonth() === today.getMonth() &&
                                unitLastUpdated.getDate() === today.getDate();

                              return !isSameDay; // Disable if not same day
                            })()}
                          />
                          <small className="form-text text-muted d-block mt-2">
                            Date from which maintenance fee billing starts for this unit.
                            {(() => {
                              const originalUnit = units.find(u => u.id === selectedUnit.id);
                              const hasExistingDate = originalUnit && originalUnit.transaction_start_date;
                              const unitLastUpdated = originalUnit?.updated_at ? new Date(originalUnit.updated_at) : null;
                              const today = new Date();
                              const isSameDay = unitLastUpdated &&
                                unitLastUpdated.getFullYear() === today.getFullYear() &&
                                unitLastUpdated.getMonth() === today.getMonth() &&
                                unitLastUpdated.getDate() === today.getDate();

                              if (hasExistingDate && !isSameDay) {
                                return (
                                  <div className="alert alert-warning mt-2 p-2">
                                    <i className="fas fa-lock me-1"></i>
                                    <strong>Locked:</strong> Transaction start date can only be changed on the same day it was first set.
                                    This prevents accidental modifications to processed financial records.
                                    <br />
                                    <small>Current date: {new Date(originalUnit.transaction_start_date).toLocaleDateString()}</small>
                                  </div>
                                );
                              }

                              if (selectedUnit.owner_id && maintenanceFee && parseFloat(maintenanceFee) > 0) {
                                return (
                                  <div className="text-info mt-1">
                                    <i className="fas fa-info-circle me-1"></i>
                                    <strong>Note:</strong> Setting this date will automatically create pending maintenance fee records
                                    from the selected month to the current month (₹{parseFloat(maintenanceFee).toLocaleString('en-IN')} per month).
                                    {hasExistingDate && (
                                      <div className="text-warning mt-1">
                                        <i className="fas fa-exclamation-triangle me-1"></i>
                                        <strong>Warning:</strong> Changing this date will remove existing auto-generated records and create new ones.
                                      </div>
                                    )}
                                  </div>
                                );
                              }

                              if (hasExistingDate) {
                                return (
                                  <div className="text-info mt-1">
                                    <i className="fas fa-calendar-check me-1"></i>
                                    You can modify this date only today. After today, it will be locked to maintain financial record integrity.
                                  </div>
                                );
                              }

                              return null;
                            })()}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Unified Save Button */}
                <div className="mb-3">
                  <button
                    className="btn btn-primary w-100"
                    onClick={handleUpdateUnit}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}



      {/* Add Unit Modal */}
      {showAddModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: settings.block_title_bg_color || '#f8f9fa', color: settings.block_title_color || '#8B4513' }}>
                <h5 className="modal-title">Add New Unit</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseAddModal}
                  style={{ backgroundImage: closeIconUrl, fontSize: '18px', opacity: '0.9' }}>
                </button>
              </div>
              <div className="modal-body">
                {error && (
                  <div className="alert alert-danger">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {error}
                  </div>
                )}
                <div className="mb-3">
                  <label htmlFor="unit_number" className="form-label">Unit Number</label>
                  <input
                    type="text"
                    className="form-control"
                    id="unit_number"
                    name="unit_number"
                    value={newUnit.unit_number}
                    onChange={(e) => setNewUnit({ ...newUnit, unit_number: e.target.value })}
                    placeholder="e.g. A-101"
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="unit_type" className="form-label">Unit Type</label>
                  <select
                    className="form-select"
                    id="unit_type"
                    name="unit_type"
                    value={newUnit.unit_type}
                    onChange={(e) => setNewUnit({ ...newUnit, unit_type: e.target.value })}
                  >
                    <option value="Unit">Unit</option>
                    <option value="Villa">Villa</option>
                    <option value="Apartment">Apartment</option>
                    <option value="Flat">Flat</option>
                    <option value="House">House</option>
                    <option value="Duplex">Duplex</option>
                    <option value="Penthouse">Penthouse</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label htmlFor="size" className="form-label">Size</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      id="size"
                      name="size"
                      value={newUnit.size ? newUnit.size.replace(' sq ft', '').replace('sq ft', '').trim() : ''}
                      onChange={(e) => setNewUnit({ ...newUnit, size: e.target.value ? `${e.target.value} sq ft` : '' })}
                      placeholder="1200"
                      min="0"
                      onInput={(e) => {
                        e.target.value = e.target.value.replace(/[^0-9]/g, '');
                      }}
                    />
                    <span className="input-group-text">sq ft</span>
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="maintenance_fee" className="form-label">Monthly Maintenance Fee</label>
                  <div className="input-group">
                    <span className="input-group-text">₹</span>
                    <input
                      type="text"
                      className="form-control"
                      id="maintenance_fee"
                      name="maintenance_fee"
                      // value={newUnit.maintenance_fee}
                      onChange={(e) => setNewUnit({ ...newUnit, maintenance_fee: e.target.value })}
                      placeholder={`e.g. ${settings.maintenance_fee || 2000}`}
                      onInput={(e) => {
                        e.target.value = e.target.value.replace(/[^0-9]/g, '');
                      }}
                    />
                    <span className="input-group-text">/month</span>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseAddModal}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={handleAddUnit}>Add Unit</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && unitToDelete && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">Confirm Delete Unit</h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowDeleteConfirmation(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete unit <strong>{unitToDelete.unit_number || unitToDelete.villa_number}</strong>?</p>
                <p className="text-danger">This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteConfirmation(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteUnit}
                  disabled={loading}
                >
                  {loading ? (
                    <span className="spinner-border spinner-border-sm"></span>
                  ) : (
                    <><i className="fas fa-trash-alt me-1"></i>Delete</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Ownership Confirmation Modal */}
      {showResetOwnershipConfirm && selectedUnit && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-warning text-dark">
                <h5 className="modal-title">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Confirm Reset Ownership
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowResetOwnershipConfirm(false)}
                  disabled={isResettingOwnership}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to reset ownership for unit{' '}
                  <strong>{selectedUnit.unit_number || selectedUnit.villa_number}</strong>?
                </p>
                <div className="alert alert-warning mb-3">
                  <strong>This action will:</strong>
                  <ul className="mb-0 mt-2">
                    <li>Remove the current owner ({selectedUnit.owner_name || 'Unknown'})</li>
                    <li>Clear the transaction start date</li>
                    <li>Reset built year and month</li>
                    <li>Set unit status to &quot;Vacant&quot;</li>
                    <li>Disable all pending transactions for this unit</li>
                  </ul>
                </div>
                <p className="text-muted mb-0">
                  <small>
                    <i className="bi bi-info-circle me-1"></i>
                    Paid and partially paid transactions will not be affected.
                  </small>
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowResetOwnershipConfirm(false)}
                  disabled={isResettingOwnership}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleResetOwnership}
                  disabled={isResettingOwnership}
                >
                  {isResettingOwnership ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1"></span>
                      Resetting...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-person-x me-1"></i>
                      Reset Ownership
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Owner Modal */}
      {showChangeOwnerModal && selectedUnit && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: primaryColor, color: '#fff' }}>
                <h5 className="modal-title">
                  <i className="bi bi-person-gear me-2"></i>
                  Change Owner
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => {
                    setShowChangeOwnerModal(false);
                    setNewOwnerId('');
                  }}
                  disabled={isChangingOwner}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Change owner for unit{' '}
                  <strong>{selectedUnit.unit_number || selectedUnit.villa_number}</strong>
                </p>
                <div className="alert alert-info mb-3">
                  <strong>Current Owner:</strong> {selectedUnit.owner_name || 'Unknown'}
                  <br />
                  <small className="text-muted">{selectedUnit.contact_email || ''}</small>
                </div>
                <div className="mb-3">
                  <label htmlFor="newOwnerSelect" className="form-label">Select New Owner <span className="text-danger">*</span></label>
                  <select
                    id="newOwnerSelect"
                    className="form-select"
                    value={newOwnerId}
                    onChange={(e) => setNewOwnerId(e.target.value)}
                    disabled={isChangingOwner}
                  >
                    <option value="">-- Select Owner --</option>
                    {unassignedOwners.map(owner => (
                            <option key={owner.id} value={owner.id}>
                              {owner.name} ({owner.email})
                            </option>
                          ))}
                  </select>
                  <small className="form-text text-muted">
                    Select a new owner to replace the current owner for this unit.
                  </small>
                </div>
                <div className="alert alert-warning mb-0">
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  <strong>Note:</strong> This will only update the owner assignment. The previous owner will no longer be associated with this unit.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowChangeOwnerModal(false);
                    setNewOwnerId('');
                  }}
                  disabled={isChangingOwner}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleChangeOwner}
                  disabled={isChangingOwner || !newOwnerId}
                >
                  {isChangingOwner ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1"></span>
                      Changing...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-person-check me-1"></i>
                      Change Owner
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <Toaster
        position="top-center"
        reverseOrder={false}
        gutter={8}
        containerClassName=""
        containerStyle={{}}
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            theme: {
              primary: '#4CAF50',
              secondary: '#fff',
            },
          },
        }}
      />
    </div>
  );
};

export default Units; 